import { Component } from '@theme/component';

export class AnnouncementBar extends Component {
  #current = 0;
  #interval;
  #animationTimer;
  #initialized = false;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #hover = window.matchMedia('(hover: hover)');

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#motion.addEventListener('change', this.#handleMotionChange);
    this.current = this.#current;
    this.resume();
    this.#syncButton();
  }

  disconnectedCallback() {
    this.suspend();
    this.#clearTransition();
    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.suspend);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#motion.removeEventListener('change', this.#handleMotionChange);
    super.disconnectedCallback();
  }

  next() { this.current += 1; }
  previous() { this.current -= 1; }

  play(interval = this.autoplayInterval) {
    this.suspend();
    if (!this.autoplay || document.hidden || this.#motion.matches || (this.refs.slides ?? []).length < 2) return;
    this.paused = false;
    this.#interval = setInterval(() => {
      if (document.hidden || this.#motion.matches || (this.#hover.matches && this.matches(':hover'))) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  togglePlayback() {
    if (this.#motion.matches) return;
    if (this.paused) this.play();
    else this.pause();
  }

  get paused() { return this.hasAttribute('paused'); }
  set paused(value) {
    this.toggleAttribute('paused', value);
    this.#syncButton();
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused || document.hidden || this.#motion.matches) return;
    this.play();
  }

  get autoplay() { return Boolean(this.autoplayInterval); }
  get autoplayInterval() {
    const value = parseInt(this.getAttribute('autoplay') || '', 10);
    return Number.isFinite(value) && value > 0 ? value * 1000 : undefined;
  }
  get current() { return this.#current; }
  set current(value) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;
    const previous = ((this.#current % slides.length) + slides.length) % slides.length;
    const active = ((value % slides.length) + slides.length) % slides.length;
    this.#clearTransition();
    if (this.#initialized && previous !== active && !this.#motion.matches) {
      slides[previous].classList.add('is-leaving');
      this.#animationTimer = setTimeout(() => this.#clearTransition(), 400);
    }
    this.#current = value;
    slides.forEach((slide, index) => slide.setAttribute('aria-hidden', String(index !== active)));
    this.#initialized = true;
  }

  #clearTransition() {
    clearTimeout(this.#animationTimer);
    this.#animationTimer = undefined;
    this.refs.slides?.forEach((slide) => slide.classList.remove('is-leaving'));
  }

  #syncButton() {
    const button = this.querySelector('[data-announcement-pause]');
    if (!button) return;
    const paused = this.paused || this.#motion.matches;
    button.setAttribute('aria-pressed', String(paused));
    button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    button.disabled = this.#motion.matches;
  }
  #handleMouseEnter = () => { if (this.#hover.matches) this.suspend(); };
  #handleFocusOut = (event) => { if (!this.contains(event.relatedTarget)) this.resume(); };
  #handleVisibilityChange = () => { if (document.hidden) this.suspend(); else this.resume(); };
  #handleMotionChange = () => {
    if (this.#motion.matches) { this.suspend(); this.#clearTransition(); }
    else this.resume();
    this.#syncButton();
  };
}

if (!customElements.get('announcement-bar-component')) customElements.define('announcement-bar-component', AnnouncementBar);
